/**
 * 透射率查找表（Transmittance LUT），CPU 预计算。
 * 参数化 (r, μ)：r 为到地心的半径，μ 为射线方向与径向的夹角余弦。
 * 存储从半径 r、方向 cos(zenith)=μ 处到大气的光学厚度（RGB）。
 * 着色器中透射率 = exp(-tau)。
 */

import * as THREE from 'three'
import type { AtmosphereCoeffs } from '../utils/atmosphereParams'

export const LUT_WIDTH = 128
export const LUT_HEIGHT = 32

/** 表示方向被地面遮挡时的“无限”光学厚度 */
export const TAU_BLOCKED = 1e4

export class TransmittanceLUT {
  readonly texture: THREE.DataTexture
  private readonly data: Float32Array
  private coeffs: AtmosphereCoeffs

  constructor(coeffs: AtmosphereCoeffs) {
    this.coeffs = coeffs
    this.data = new Float32Array(LUT_WIDTH * LUT_HEIGHT * 4)
    this.texture = new THREE.DataTexture(
      this.data,
      LUT_WIDTH,
      LUT_HEIGHT,
      THREE.RGBAFormat,
      THREE.FloatType,
    )
    this.texture.minFilter = THREE.LinearFilter
    this.texture.magFilter = THREE.LinearFilter
    this.texture.wrapS = THREE.ClampToEdgeWrapping
    this.texture.wrapT = THREE.ClampToEdgeWrapping
    this.compute()
  }

  /** 系数变更时重算（计算量小，直接全量重算） */
  update(coeffs: AtmosphereCoeffs): void {
    this.coeffs = coeffs
    this.compute()
    this.texture.needsUpdate = true
  }

  private compute(): void {
    const { R, Rt, betaR, betaMExt, Hr, Hm } = this.coeffs
    const SAMPLES = 48
    const dR = Rt - R

    for (let j = 0; j < LUT_HEIGHT; j++) {
      // v: 0=大气顶, 1=地表
      const v = j / (LUT_HEIGHT - 1)
      const r = Rt - dR * v
      for (let i = 0; i < LUT_WIDTH; i++) {
        const mu = (i / (LUT_WIDTH - 1)) * 2 - 1 // -1..1
        let tau: [number, number, number] = [0, 0, 0]

        if (mu < 0) {
          // 向下方向：若射线撞上地面则被遮挡，透射率为 0
          const disc = r * r * mu * mu - (r * r - R * R)
          if (disc >= 0) {
            const tG = -r * mu - Math.sqrt(disc)
            if (tG >= 0) tau = [TAU_BLOCKED, TAU_BLOCKED, TAU_BLOCKED]
          }
        }

        if (tau[0] < TAU_BLOCKED) {
          // 沿射线方向到大气顶的光学深度
          const tTop = -r * mu + Math.sqrt(r * r * mu * mu - (r * r - Rt * Rt))
          const ds = tTop / SAMPLES
          for (let s = 0; s < SAMPLES; s++) {
            const t = (s + 0.5) * ds
            const rad = Math.sqrt(r * r + 2 * r * mu * t + t * t)
            const h = Math.max(rad - R, 0)
            const rhoR = Math.exp(-h / Hr)
            const rhoM = Math.exp(-h / Hm)
            tau[0] += (betaR[0] * rhoR + betaMExt[0] * rhoM) * ds
            tau[1] += (betaR[1] * rhoR + betaMExt[1] * rhoM) * ds
            tau[2] += (betaR[2] * rhoR + betaMExt[2] * rhoM) * ds
          }
        }

        const o = (j * LUT_WIDTH + i) * 4
        this.data[o] = tau[0]
        this.data[o + 1] = tau[1]
        this.data[o + 2] = tau[2]
        this.data[o + 3] = 1
      }
    }
  }
}
